import { type DriveFile } from '@/shared/DriveTypes';
import { Backdrop, Modal, Popover } from '@mui/material';
import cx from 'classnames';
import { bindMenu, usePopupState } from 'material-ui-popup-state/hooks';
import { useRef } from 'react';
import { Link } from 'wouter';
import { darkTheme, lightTheme } from '~/theme.css';
import { isBun, LEGACY } from '~/utils/constants';
import { useKeyPressRaw } from '~/utils/hooks/hooks';
import registerComponent from '~/utils/registerComponent';
import { useTheme } from '~/utils/store';
import { type Profile } from '~/utils/types';
import { isInputEvent, isModalEvent } from '~/utils/utils';
import * as headerStyles from '../Header/Header.css';
import { ShareModal } from './ShareModal';

/**
 * Used by legacy header share button
 */
export const ShareModalButton = ({
  wiki,
  wikis,
  files,
  updateFile,
}: {
  wiki: DriveFile;
  wikis: DriveFile[];
  files: DriveFile[];
  user: Profile | undefined;
  // Legacy
  updateFile?: (payload: Partial<DriveFile>) => void;
}) => {
  const sharePopupState = usePopupState({ variant: 'popover' });
  const theme = useTheme();
  const linkRef = useRef<HTMLAnchorElement>(null);
  useKeyPressRaw(
    's',
    () => {
      sharePopupState.setAnchorEl(linkRef.current);
      sharePopupState.toggle();
    },
    (e) => {
      if (sharePopupState.isOpen && isModalEvent(e)) return true;
      if (isInputEvent(e)) return false;
      return true;
    }
  );

  return (
    <>
      <Link
        href=""
        className={LEGACY ? headerStyles.legacyLabel : headerStyles.label}
        style={
          LEGACY
            ? {
                marginRight: '.5rem',
              }
            : undefined
        }
        ref={linkRef}
        onClick={(e) => {
          e.preventDefault();
          sharePopupState.setAnchorEl(e.currentTarget);
          sharePopupState.open();
        }}
      >
        Share
      </Link>
      {wiki && (
        <Popover
          {...bindMenu(sharePopupState)}
          onClose={() => {
            sharePopupState.close();
          }}
          className={cx({
            [darkTheme]: theme === 'dark',
            [lightTheme]: theme === 'light',
            [darkTheme]: theme === 'dark',
            [lightTheme]: theme === 'light',
          })}
          slotProps={{
            paper: {
              style: {
                marginTop: '1rem',
                marginLeft: LEGACY ? 14 : 2,
                color: 'inherit',
                boxShadow: theme === 'light' ? 'rgba(0, 0, 0, 0.2) 2px 2px 8px 0' : undefined,
                backgroundColor: 'var(--root-bg-color)',
                borderRadius: 10,
              },
              sx: {
                overflowX: 'unset',
                overflowY: 'unset',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -8,
                  right: 26,
                  width: 16,
                  height: 16,
                  backgroundColor: theme === 'light' ? '#fff' : 'inherit',
                  boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 4px 0',
                  transform: 'rotate(-45deg)',
                  clipPath: 'polygon(-5px -5px, calc(100% + 5px) -5px, calc(100% + 5px) calc(100% + 5px))',
                },
              },
            },
          }}
          transitionDuration={{
            enter: isBun ? 0 : 200,
            exit: 0,
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <ShareModal
            onCancel={() => sharePopupState.close()}
            file={wiki}
            wiki={wiki}
            wikis={wikis}
            files={files}
            popover
            updateFile={updateFile}
          />
        </Popover>
      )}
    </>
  );
};

/**
 * Used by legacy tree component
 */
export const ShareModalContainer = ({
  file,
  files,
  wiki,
  wikis,
  updateFile,
  onClose,
}: Parameters<typeof ShareModal>[0] & {
  // Legacy
  updateFile?: (payload: Partial<DriveFile>) => void;
  onClose?: () => void;
}) => {
  const theme = useTheme();

  return (
    <Modal
      open
      {...(onClose && { onClose })}
      role="dialog"
      className={cx({
        [darkTheme]: theme === 'dark',
        [lightTheme]: theme === 'light',
      })}
      slots={{
        backdrop: Backdrop,
      }}
      slotProps={{
        backdrop: {
          timeout: 0,
        },
      }}
    >
      <ShareModal
        file={file}
        files={files}
        wiki={wiki}
        wikis={wikis}
        onCancel={() => onClose?.()}
        updateFile={updateFile}
      />
    </Modal>
  );
};

if (LEGACY) {
  registerComponent(ShareModalButton, 'wc-share-modal-button', {
    wiki: 'json',
    wikis: 'json',
    files: 'json',
    updateFile: 'function',
  });
}

if (LEGACY) {
  registerComponent(ShareModalContainer, 'wc-share-modal', {
    file: 'json',
    files: 'json',
    wiki: 'json',
    wikis: 'json',
    updateFile: 'function',
    onClose: 'function',
  });
}
